import { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from "react";
import { JobCard } from "./JobCard";
import { supabase, ensureCategoriesTable, fixCategoryTableIssues } from "@/lib/supabase";
import { EditJobDialog } from "./EditJobDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { commonStyles } from "@/lib/theme";
import { Plus, ChevronDown, ChevronUp, SlidersHorizontal, Tag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

export type JobStatus = "Interested" | "Applied" | "Interview" | "Offer" | "Rejected" | "Archived";

export const statusOrder: JobStatus[] = ["Interested", "Applied", "Interview", "Offer", "Rejected", "Archived"];

export interface Job {
  id: string;
  url: string;
  company_name: string;
  job_title: string;
  description: string;
  status: JobStatus;
  date_added: string;
  deadline?: string;
  logoUrl?: string;
  user_id: string;
}

type SortOption = {
  label: string;
  value: string;
  sortFn: (a: Job, b: Job) => number;
};

const sortOptions: SortOption[] = [
  {
    label: "Newest First",
    value: "newest",
    sortFn: (a, b) => new Date(b.date_added).getTime() - new Date(a.date_added).getTime()
  },
  {
    label: "Oldest First",
    value: "oldest",
    sortFn: (a, b) => new Date(a.date_added).getTime() - new Date(b.date_added).getTime()
  },
  {
    label: "Deadline Soonest",
    value: "deadline-asc",
    sortFn: (a, b) => {
      if (!a.deadline && !b.deadline) return 0;
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    }
  },
  {
    label: "Deadline Latest",
    value: "deadline-desc",
    sortFn: (a, b) => {
      if (!a.deadline && !b.deadline) return 0;
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(b.deadline).getTime() - new Date(a.deadline).getTime();
    }
  }
];

interface JobBoardProps {
  demoMode?: boolean;
  demoJobs?: Array<{
    id: number;
    company_name: string;
    job_title: string;
    description: string;
    status: string;
    date_added: string;
    url: string;
  }>;
  isDarkMode?: boolean;
  onDeleteJob?: (jobId: string | number) => void;
  onUpdateJob?: (job: any) => void;
}

interface Category {
  id: string;
  name: string;
  user_id: string;
}

export const JobBoard = forwardRef<{ fetchJobs: () => void }, JobBoardProps>(({ 
  demoMode, 
  demoJobs, 
  isDarkMode = false,
  onDeleteJob,
  onUpdateJob
}, ref) => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>("newest");
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<JobStatus | "All">("All");
  const { toast } = useToast();
  const { user } = useAuth();
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [jobCategoryMappings, setJobCategoryMappings] = useState<Record<string, string[]>>({});
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [categoriesCollapsed, setCategoriesCollapsed] = useState(true);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isEditCategoryDialogOpen, setIsEditCategoryDialogOpen] = useState(false);
  const [isDeleteCategoryDialogOpen, setIsDeleteCategoryDialogOpen] = useState(false);

  const fetchJobs = async () => {
    if (demoMode && demoJobs) {
      console.log('Setting demo jobs:', demoJobs);
      setJobs(demoJobs);
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching jobs for user:', user?.id);
      
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('user_id', user?.id)
        .order('date_added', { ascending: false });

      if (error) {
        console.error('Error fetching jobs:', error);
        throw error;
      }
      
      console.log('Jobs fetched:', data?.length || 0, data);
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast({
        title: "Error fetching jobs",
        description: "Could not load your jobs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (demoMode) {
      setCategories([
        { id: 'demo-1', name: 'UX Design', user_id: 'demo-user' },
        { id: 'demo-2', name: 'Frontend', user_id: 'demo-user' },
        { id: 'demo-3', name: 'Remote Jobs', user_id: 'demo-user' }
      ]);
      return;
    }

    if (!user) {
      console.log('No user found, cannot fetch categories');
      return;
    }

    try {
      console.log('Fetching categories for user:', user.id);
      
      const { data, error } = await supabase
        .from('job_categories')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }
      
      console.log('Categories fetched:', data?.length || 0, data);
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Error loading categories",
        description: "Could not load your categories. Please refresh the page.",
        variant: "destructive",
      });
    }
  };

  const fetchJobCategoryMappings = async () => {
    if (demoMode) {
      setJobCategoryMappings({
        1: ['demo-1'],
        2: ['demo-2'],
        3: ['demo-3']
      });
      return;
    }

    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('job_category_mappings')
        .select('*');
      
      if (error) throw error;
      
      const mappings: Record<string, string[]> = {};
      data?.forEach(mapping => {
        if (!mappings[mapping.job_id]) {
          mappings[mapping.job_id] = [];
        }
        mappings[mapping.job_id].push(mapping.category_id);
      });
      
      setJobCategoryMappings(mappings);
    } catch (error) {
      console.error('Error fetching job category mappings:', error);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchCategories();
    fetchJobCategoryMappings();
  }, []);

  // Add effect to update jobs when demoJobs changes
  useEffect(() => {
    if (demoMode && demoJobs) {
      console.log('Demo jobs updated:', demoJobs);
      setJobs(demoJobs);
    }
  }, [demoMode, demoJobs]);

  useEffect(() => {
    // Re-sort jobs when sort option changes
    const sortedJobs = [...jobs].sort(
      sortOptions.find(option => option.value === sortBy)?.sortFn ||
      sortOptions[0].sortFn
    );
    setJobs(sortedJobs);
  }, [sortBy]);

  useImperativeHandle(ref, () => ({
    fetchJobs
  }));

  const handleDelete = async (id: string) => {
    if (demoMode && onDeleteJob) {
      onDeleteJob(id);
      return;
    }

    try {
    const { error } = await supabase
      .from('jobs')
      .delete()
      .eq('id', id);

      if (error) throw error;

      await fetchJobs();
      toast({
        title: "Job deleted",
        description: "The job has been removed from your board.",
      });
    } catch (error) {
      console.error('Error deleting job:', error);
      toast({
        title: "Error deleting job",
        description: "Could not delete the job. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (jobId: string, newStatus: JobStatus) => {
    if (demoMode && onUpdateJob) {
      const jobToUpdate = jobs.find(job => job.id === jobId);
      if (jobToUpdate) {
        onUpdateJob({ ...jobToUpdate, status: newStatus });
      }
      return;
    }

    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: newStatus })
        .eq('id', jobId);

      if (error) throw error;

      await fetchJobs();
      toast({
        title: "Status updated",
        description: `Job status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error updating status",
        description: "Could not update the job status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (updatedJob: Job) => {
    // When in demo mode, use the onUpdateJob callback from props
    if (demoMode && onUpdateJob) {
      onUpdateJob(updatedJob);
      return;
    }
    
    // When edit is triggered, set the job to edit and open the dialog
    setEditingJob(updatedJob);
    setIsEditDialogOpen(true);
  };

  const filteredJobs = useMemo(() => {
    let result = [...jobs];
    
    if (activeFilter !== "All") {
      result = result.filter(job => job.status === activeFilter);
    } else {
      result = result.filter(job => job.status !== "Archived");
    }
    
    if (selectedCategory) {
      result = result.filter(job => {
        const jobCategories = jobCategoryMappings[job.id] || [];
        return jobCategories.includes(selectedCategory);
  });
    }
    
    return result;
  }, [jobs, activeFilter, selectedCategory, jobCategoryMappings]);

  const checkAndArchiveJobs = async (jobsToCheck: Job[]) => {
    const today = new Date();
    const jobsToArchive = jobsToCheck.filter(job => 
      job.status !== "Archived" && 
      job.status !== "Applied" &&
      job.deadline && 
      new Date(job.deadline) < today
    );

    if (jobsToArchive.length > 0 && !demoMode) {
      const { error } = await supabase
        .from('jobs')
        .update({ status: "Archived" })
        .in('id', jobsToArchive.map(job => job.id));

      if (error) {
        console.error('Error archiving jobs:', error);
        toast({
          title: "Error archiving jobs",
          description: "Could not archive expired jobs.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Jobs Archived",
        description: `${jobsToArchive.length} expired ${jobsToArchive.length === 1 ? 'job has' : 'jobs have'} been archived.`,
      });

      fetchJobs();
    }
  };

  useEffect(() => {
    if (jobs.length > 0) {
      checkAndArchiveJobs(jobs);
    }
  }, [jobs]);

  // Set the category name when editing
  useEffect(() => {
    if (editingCategory) {
      setNewCategoryName(editingCategory.name);
    } else {
      setNewCategoryName("");
    }
  }, [editingCategory]);

  const handleCategoryChange = async (jobId: string, categoryId: string) => {
    if (demoMode) {
      setJobCategoryMappings(prev => {
        const newMappings = { ...prev };
        
        if (categoryId === "none") {
          newMappings[jobId] = [];
        } else {
          newMappings[jobId] = [categoryId];
        }
        
        return newMappings;
      });
      
      toast({
        title: "Demo Mode",
        description: "Category assignment updated in demo mode (not saved to database).",
      });
      return;
    }

    if (!user) return;

    try {
      if (categoryId === "none") {
        const { error } = await supabase
          .from('job_category_mappings')
          .delete()
          .eq('job_id', jobId);
        
        if (error) throw error;
      } else {
        await supabase
          .from('job_category_mappings')
          .delete()
          .eq('job_id', jobId);
        
        const { error } = await supabase
          .from('job_category_mappings')
          .insert({
            job_id: jobId,
            category_id: categoryId
          });
        
        if (error) throw error;
      }
      
      fetchJobCategoryMappings();
      
      toast({
        title: "Category Updated",
        description: "Job category assignment updated successfully.",
      });
    } catch (error) {
      console.error('Error updating job category:', error);
      toast({
        title: "Error updating category",
        description: "Could not update the job category. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    console.log('Adding category:', newCategoryName);
    console.log('demoMode:', demoMode);
    console.log('User:', user?.id);

    if (demoMode) {
      const newCategory = {
        id: `demo-${Date.now()}`,
        name: newCategoryName,
        user_id: 'demo-user'
      };
      setCategories(prev => [...prev, newCategory]);
      setNewCategoryName("");
      setShowCategoryDialog(false);
      
      toast({
        title: "Demo Mode",
        description: "Category added in demo mode (not saved to database).",
      });
      return;
    }

    if (!user) {
      console.log('No user found, cannot add category');
      toast({
        title: "Authentication required",
        description: "Please sign in to add categories.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('Ensuring categories table exists...');
      const tablesReady = await ensureCategoriesTable();
      
      if (!tablesReady) {
        console.error('Categories table could not be created or verified');
        toast({
          title: "Database setup error",
          description: "Could not set up the categories table. Please try again later.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Categories table is ready, proceeding with insert');

      // Get the current session to ensure we have the exact auth.uid() value
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Error getting session:', sessionError);
        toast({
          title: "Authentication error",
          description: "Could not verify your session. Please try signing in again.",
          variant: "destructive",
        });
        return;
      }
      
      if (!sessionData.session?.user?.id) {
        console.error('No user ID in session');
        toast({
          title: "Authentication error",
          description: "Could not identify your user account. Please try signing in again.",
          variant: "destructive",
        });
        return;
      }
      
      // Use the session user ID directly from the session to match auth.uid() exactly
      const userIdFromSession = sessionData.session.user.id;
      console.log('Using session user ID:', userIdFromSession);
      
      const categoryData = {
        name: newCategoryName,
        user_id: userIdFromSession
      };
      
      console.log('Category data:', categoryData);
      
      // Try inserting without returning data first to isolate any issues
      const { error: insertError } = await supabase
        .from('job_categories')
        .insert(categoryData);

      if (insertError) {
        console.error('Database error adding category (insert phase):', insertError);
        console.error('Error details:', JSON.stringify(insertError, null, 2));
        
        if (insertError.code === '23502') { // not-null constraint violation
          toast({
            title: "Error adding category",
            description: `Missing required field(s): ${insertError.message}`,
            variant: "destructive",
          });
        } else if (insertError.code === '42P01') { // undefined table
          toast({
            title: "Database error",
            description: "The categories table does not exist. This is likely a database setup issue.",
            variant: "destructive",
          });
        } else if (insertError.code === '42501') { // permission denied
          toast({
            title: "Permission denied",
            description: "You don't have permission to add categories.",
            variant: "destructive",
          });
        } else if (insertError.message.includes("violates row-level security policy")) {
          console.log("RLS policy violation detected, attempting to fix table issues...");
          
          // Try to fix the RLS policy issue
          const fixResult = await fixCategoryTableIssues();
          
          if (fixResult) {
            console.log("RLS policy fixed, retrying category insertion");
            
            // Try again with the same data after fixing the RLS policy
            const { error: retryError } = await supabase
              .from('job_categories')
              .insert(categoryData);
              
            if (retryError) {
              console.error('Error in retry after fixing RLS:', retryError);
              toast({
                title: "Error adding category",
                description: "Still unable to add category after fixing permissions.",
                variant: "destructive",
              });
              throw retryError;
            }
            
            toast({
              title: "Category Added",
              description: "Your new category has been added successfully after fixing permissions.",
            });
            
            // If the insert was successful, fetch the newly created category
            const { data: newData } = await supabase
              .from('job_categories')
              .select('*')
              .eq('name', newCategoryName)
              .eq('user_id', userIdFromSession)
              .limit(1);
              
            if (newData && newData.length > 0) {
              setCategories(prev => [...prev, ...newData]);
            }
            
            setNewCategoryName("");
            setShowCategoryDialog(false);
            return;
          } else {
            toast({
              title: "RLS Policy Error",
              description: "Could not fix the row-level security policy. Please contact support.",
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Error adding category",
            description: `Database error: ${insertError.message}`,
            variant: "destructive",
          });
        }
        throw insertError;
      }
      
      // If insert succeeded, fetch the newly created category
      const { data: newData, error: selectError } = await supabase
        .from('job_categories')
        .select('*')
        .eq('name', newCategoryName)
        .eq('user_id', userIdFromSession)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (selectError) {
        console.error('Error retrieving newly added category:', selectError);
        console.error('Error details:', JSON.stringify(selectError, null, 2));
      }
      
      console.log('Category successfully added:', newData);
      if (newData && newData.length > 0) {
        setCategories(prev => [...prev, ...newData]);
      }
      setNewCategoryName("");
      setShowCategoryDialog(false);
      
      toast({
        title: "Category Added",
        description: "Your new category has been added successfully.",
      });
    } catch (error) {
      console.error('Error adding category:', error);
      // Log more detailed error information
      if (error instanceof Error) {
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      } else {
        console.error('Unknown error type:', typeof error);
        console.error('Error stringified:', JSON.stringify(error, null, 2));
      }
      
      toast({
        title: "Error adding category",
        description: error instanceof Error 
          ? `Could not add the new category: ${error.message}`
          : "Could not add the new category. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditCategory = async (categoryId: string, newName: string) => {
    if (!newName.trim()) return;

    if (demoMode) {
      setCategories(prev => 
        prev.map(cat => cat.id === categoryId ? { ...cat, name: newName } : cat)
      );

      toast({
        title: "Demo Mode",
        description: "Category updated in demo mode (not saved to database).",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to edit categories.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('job_categories')
        .update({ name: newName })
        .eq('id', categoryId)
        .eq('user_id', user.id);

      if (error) throw error;

      fetchCategories();
      
      toast({
        title: "Category Updated",
        description: "Your category has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Error updating category",
        description: "Could not update the category. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (demoMode) {
      setCategories(prev => prev.filter(cat => cat.id !== categoryId));
      
      // Also remove this category from all job-category mappings
      setJobCategoryMappings(prev => {
        const newMappings = { ...prev };
        Object.keys(newMappings).forEach(jobId => {
          newMappings[jobId] = newMappings[jobId].filter(id => id !== categoryId);
        });
        return newMappings;
      });

      toast({
        title: "Demo Mode",
        description: "Category deleted in demo mode (not saved to database).",
      });
      
      // Reset selected category if it was the one deleted
      if (selectedCategory === categoryId) {
        setSelectedCategory(null);
      }
      
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to delete categories.",
        variant: "destructive",
      });
      return;
    }

    try {
      // First delete all mappings for this category
      const { error: mappingError } = await supabase
        .from('job_category_mappings')
        .delete()
        .eq('category_id', categoryId);

      if (mappingError) throw mappingError;

      // Then delete the category itself
      const { error } = await supabase
        .from('job_categories')
        .delete()
        .eq('id', categoryId)
        .eq('user_id', user.id);

      if (error) throw error;

      fetchCategories();
      fetchJobCategoryMappings();
      
      // Reset selected category if it was the one deleted
      if (selectedCategory === categoryId) {
        setSelectedCategory(null);
      }
      
      toast({
        title: "Category Deleted",
        description: "Your category has been deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error deleting category",
        description: "Could not delete the category. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4 mb-4">
        {/* Mobile toggle button for filters - only visible on small screens */}
        <div className="flex md:hidden justify-between items-center mb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFiltersCollapsed(!filtersCollapsed)}
            className={cn(
              "w-full flex justify-between items-center",
              isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] border-[#818881]" 
                : "text-gray-600 hover:bg-[#CCF751]/90 hover:text-[#2C2E2D]"
            )}
          >
            <div className="flex items-center">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              <span>Status Filters</span>
            </div>
            {filtersCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
        </div>
        
        {/* Filters - always visible on desktop, collapsible on mobile */}
        <div className={cn(
          "flex flex-wrap gap-2",
          filtersCollapsed ? "hidden md:flex" : "flex"
        )}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveFilter("All")}
            className={cn(
              commonStyles.button.filter,
              activeFilter === "All" && commonStyles.button.filterActive
            )}
          >
            All Active
          </Button>
          {statusOrder
            .filter(status => status !== "Archived")
            .map((status) => (
              <Button
                key={status}
                variant="outline"
                size="sm"
                onClick={() => setActiveFilter(status)}
                className={cn(
                  commonStyles.button.filter,
                  activeFilter === status && commonStyles.button.filterActive
                )}
              >
                {status}
              </Button>
            ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveFilter("Archived")}
            className={cn(
              commonStyles.button.filter,
              "border-dashed",
              activeFilter === "Archived" && commonStyles.button.filterActive
            )}
          >
            Archive
            {jobs.filter(j => j.status === "Archived").length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-700">
                {jobs.filter(j => j.status === "Archived").length}
              </span>
            )}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortBy(sortBy === "newest" ? "oldest" : "newest")}
            className={commonStyles.button.filter}
          >
            Sort by Date {sortBy === "newest" ? "↓" : "↑"}
          </Button>
        </div>
        
        {/* Mobile toggle button for categories - only visible on small screens */}
        <div className="flex md:hidden justify-between items-center mt-2 mb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCategoriesCollapsed(!categoriesCollapsed)}
            className={cn(
              "w-full flex justify-between items-center",
              isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] border-[#818881]" 
                : "text-gray-600 hover:bg-[#CCF751]/90 hover:text-[#2C2E2D]"
            )}
          >
            <div className="flex items-center">
              <Tag className="h-4 w-4 mr-2" />
              <span>Categories</span>
            </div>
            {categoriesCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
        </div>
        
        {/* Categories - always visible on desktop, collapsible on mobile */}
        <div className={cn(
          "flex flex-wrap items-center gap-2 mb-4",
          categoriesCollapsed ? "hidden md:flex" : "flex"
        )}>
          <div className="flex items-center gap-2 mr-2">
            <span className={cn(
              "text-sm font-medium",
              isDarkMode ? "text-[#D9D9D9]" : "text-gray-700"
            )}>
              Categories:
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCategoryDialog(true)}
              className={cn(
                "h-8 px-2",
                isDarkMode 
                  ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] border-[#818881]" 
                  : "text-gray-600 hover:bg-[#CCF751]/90 hover:text-[#2C2E2D]"
              )}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          </div>
          
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(null)}
            className={cn(
              "h-8",
              selectedCategory === null
                ? "bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
                : isDarkMode
                  ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751] hover:text-[#2C2E2D] border-[#818881]"
                  : "bg-white text-gray-700 hover:bg-[#CCF751] hover:text-[#2C2E2D]"
            )}
          >
            All
          </Button>
          
          {categories.map(category => (
            <div key={category.id} className="relative group">
              <Button
                variant={selectedCategory === category.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category.id)}
                className={cn(
                  "h-8",
                  selectedCategory === category.id
                    ? "bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
                    : isDarkMode
                      ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751] hover:text-[#2C2E2D] border-[#818881]"
                      : "bg-white text-gray-700 hover:bg-[#CCF751] hover:text-[#2C2E2D]"
                )}
              >
                {category.name}
              </Button>
              <div className="absolute right-0 top-0 h-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                <div className="flex h-full">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingCategory(category);
                      setIsEditCategoryDialogOpen(true);
                    }}
                    className="h-full px-1 flex items-center justify-center text-[#CCF751] hover:text-[#CCF751]"
                  >
                    <div className="h-6 w-6 rounded-full bg-[#333433]/80 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingCategory(category);
                      setIsDeleteCategoryDialogOpen(true);
                    }}
                    className="h-full px-1 flex items-center justify-center text-rose-400 hover:text-rose-400"
                  >
                    <div className="h-6 w-6 rounded-full bg-[#333433]/80 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex justify-end">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className={cn("w-[180px]", commonStyles.button.filter)}>
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent className={commonStyles.dropdown}>
              {sortOptions.map(option => (
                <SelectItem 
                  key={option.value} 
                  value={option.value}
                  className={commonStyles.dropdownItem}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredJobs.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onStatusChange={() => fetchJobs()}
            categories={categories}
            jobCategories={jobCategoryMappings[job.id] || []}
            onCategoryChange={handleCategoryChange}
            isDarkMode={isDarkMode}
          />
        ))}
        {filteredJobs.length === 0 && (
          <div className="col-span-3 text-center p-8">
            <p className={isDarkMode ? "text-[#818881]" : "text-gray-500"}>
              No jobs found in this category. Add a job or change your filter.
            </p>
          </div>
        )}
      </div>

      <EditJobDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        job={editingJob}
        onJobUpdated={(updatedJob) => {
          if (updatedJob && demoMode && onUpdateJob) {
            // In demo mode, use the onUpdateJob callback
            onUpdateJob(updatedJob);
          } else {
            // In regular mode, refresh the jobs list
          fetchJobs();
          }
          setIsEditDialogOpen(false);
        }}
        isDarkMode={isDarkMode}
      />

      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className={cn(
          "max-w-md",
          isDarkMode ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]" : "bg-white"
        )}>
          <DialogHeader>
            <DialogTitle className={cn(
              "text-xl font-semibold",
              isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"
            )}>
              Add New Category
            </DialogTitle>
            <DialogDescription className={cn(
              isDarkMode ? "text-[#818881]" : "text-gray-500"
            )}>
              Create a custom category to organize your job applications.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddCategory} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="categoryName" className={isDarkMode ? "text-[#D9D9D9]" : ""}>
                Category Name
              </Label>
              <Input
                id="categoryName"
                placeholder="e.g., UX Design, Remote Jobs, etc."
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className={cn(
                  isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]" : "bg-white"
                )}
              />
            </div>
            
            <div className="flex justify-end gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCategoryDialog(false);
                  setNewCategoryName("");
                }}
                className={cn(
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] border-[#818881]"
                    : "text-gray-600 hover:bg-[#CCF751]/90 hover:text-[#2C2E2D]"
                )}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!newCategoryName.trim()}
                className="bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
              >
                Add Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={isEditCategoryDialogOpen} onOpenChange={setIsEditCategoryDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          isDarkMode ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]" : "bg-white"
        )}>
          <DialogHeader>
            <DialogTitle className={cn(
              "text-xl font-semibold",
              isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"
            )}>
              Edit Category
            </DialogTitle>
            <DialogDescription className={cn(
              isDarkMode ? "text-[#818881]" : "text-gray-500"
            )}>
              Update your category name.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            if (editingCategory) {
              handleEditCategory(editingCategory.id, newCategoryName);
              setIsEditCategoryDialogOpen(false);
              setEditingCategory(null);
              setNewCategoryName("");
            }
          }} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editCategoryName" className={isDarkMode ? "text-[#D9D9D9]" : ""}>
                Category Name
              </Label>
              <Input
                id="editCategoryName"
                placeholder="e.g., UX Design, Remote Jobs, etc."
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className={cn(
                  isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]" : "bg-white"
                )}
              />
            </div>
            
            <div className="flex justify-end gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditCategoryDialogOpen(false);
                  setNewCategoryName("");
                  setEditingCategory(null);
                }}
                className={cn(
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] border-[#818881]"
                    : "text-gray-600 hover:bg-[#CCF751]/90 hover:text-[#2C2E2D]"
                )}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!newCategoryName.trim()}
                className="bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
              >
                Update Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <AlertDialog open={isDeleteCategoryDialogOpen} onOpenChange={setIsDeleteCategoryDialogOpen}>
        <AlertDialogContent className={cn(
          isDarkMode ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]" : "bg-white"
        )}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDarkMode ? "text-[#D9D9D9]" : ""}>
              Delete Category
            </AlertDialogTitle>
            <AlertDialogDescription className={isDarkMode ? "text-[#818881]" : ""}>
              Are you sure you want to delete this category?
              {editingCategory && (
                <span className="font-medium block mt-2">
                  "{editingCategory.name}"
                </span>
              )}
              <p className="mt-2">
                This will remove the category and unassign it from all jobs. This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={isDarkMode 
                ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] border-[#818881]"
                : "text-gray-600 hover:bg-[#CCF751]/90 hover:text-[#2C2E2D]"
              }
              onClick={() => {
                setIsDeleteCategoryDialogOpen(false);
                setEditingCategory(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 text-white hover:bg-red-600"
              onClick={() => {
                if (editingCategory) {
                  handleDeleteCategory(editingCategory.id);
                  setIsDeleteCategoryDialogOpen(false);
                  setEditingCategory(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});